/**
 * Connectivity transitions for connectivity.store. Browser online/offline events
 * and a periodic reachability poll drive isOnline/isSyncing through a debounce
 * window and a brief "syncing" flash. Extracted from persist-gate so the timer
 * behavior is unit-testable (see connectivity.controller.test.ts); the wiring is
 * started on mount via startConnectivityMonitoring, not at import.
 */
import onlineCheck from 'is-online';

import { getOnlineValue, setIsOnline, setIsSyncing } from './connectivity.store';

const ONLINE_DEBOUNCE_MS = 5 * 1000;
const POLL_INTERVAL_MS = 10 * 1000;
const ONLINE_EVENT_FLASH_MS = 3000;
const POLL_FLASH_MS = 5000;

let syncFlashTimer: ReturnType<typeof setTimeout> | undefined;
let onlineEventTimer: ReturnType<typeof setTimeout> | null = null;
let pollDebounceTimer: ReturnType<typeof setTimeout> | null = null;
let pollTimer: ReturnType<typeof setInterval> | null = null;

const clearTimer = (timer: ReturnType<typeof setTimeout> | null | undefined) => {
  if (timer) clearTimeout(timer);
};

// Show the "syncing" banner, then clear it after the flash window.
const flashSyncing = (durationMs: number) => {
  setIsSyncing(true);
  clearTimer(syncFlashTimer);
  syncFlashTimer = setTimeout(() => setIsSyncing(false), durationMs);
};

export const handleBrowserOffline = (): void => {
  setIsOnline(false);
  setIsSyncing(false);
  clearTimer(onlineEventTimer);
  onlineEventTimer = null;
  clearTimer(pollDebounceTimer);
  pollDebounceTimer = null;
};

// Debounce the browser 'online' event before trusting it, then flash syncing.
export const handleBrowserOnline = (): void => {
  if (getOnlineValue()) return;
  clearTimer(onlineEventTimer);
  clearTimer(pollDebounceTimer);
  pollDebounceTimer = null;

  onlineEventTimer = setTimeout(() => {
    setIsOnline(true);
    flashSyncing(ONLINE_EVENT_FLASH_MS);
    onlineEventTimer = null;
  }, ONLINE_DEBOUNCE_MS);
};

// Reachability poll: confirm a regained connection (debounced) or a lost one.
export const handleConnectivityPoll = async (): Promise<void> => {
  const nextOnline = await onlineCheck();
  if (!getOnlineValue() && nextOnline && !pollDebounceTimer) {
    clearTimer(onlineEventTimer);
    onlineEventTimer = null;
    pollDebounceTimer = setTimeout(() => {
      flashSyncing(POLL_FLASH_MS);
      setIsOnline(nextOnline);
      pollDebounceTimer = null;
    }, ONLINE_DEBOUNCE_MS);
  } else if (getOnlineValue() && !nextOnline) {
    clearTimer(pollDebounceTimer);
    pollDebounceTimer = null;
    clearTimer(onlineEventTimer);
    onlineEventTimer = null;
    setIsOnline(false);
    setIsSyncing(false);
  }
};

// Idempotent: wires the browser listeners + poll once. Started on mount.
export const startConnectivityMonitoring = (): void => {
  if (pollTimer) return;
  window.addEventListener('offline', handleBrowserOffline);
  window.addEventListener('online', handleBrowserOnline);
  pollTimer = setInterval(handleConnectivityPoll, POLL_INTERVAL_MS);
};
